<template>
  <div class="diagnose">
    <v-card flat class="ma-2 pa-2 card-rounded">
      <v-card-title>
        <h3 class="primary--text">Cirurgias - Geral Região Anatómica</h3>
      </v-card-title>
    </v-card>
    <table
      :style="cssVars"
      :class="style.borders ? 'bordered' : ''"
      class="combined-report styled-table selectable"
    >
      <thead>
        <tr>
          <th style="min-width: 150px" class="header-title"></th>
          <th class="header-title" v-for="row in tableItems" :key="row.id">
            <span class="" v-text="row.label"></span>
          </th>
          <th style="min-width: 150px" class="header-title">Total</th>
        </tr>
      </thead>
      <template v-for="(item, key) in userAnatomicalGroups">
        <tr :key="key" style="background-color: #d6eeee">
          <td class="categ-title">
            {{ item.text }}
          </td>
          <td v-for="row in tableItems" :key="row.id">
            {{ getGroupCount(item.id, row.id) }}
          </td>
          <td>{{ getGroupTotal(item.id) }}</td>
        </tr>
        <tr
          :class="key % 2 == 0 ? 'even' : 'odd'"
          v-for="(row, index) in item.options"
          :key="'' + key + '' + index"
        >
          <td class="categ-title" :rowspan="row.rowspan">{{ row.text }}</td>
          <td
            v-for="value in tableItems"
            :key="'' + key + '' + index + value.id"
          >
            {{ getValue(value.id, row.value) }}
          </td>
          <td>{{ getTotal(row.value) }}</td>
        </tr>
      </template>
      <tr>
        <td class="total">Total</td>
        <td class="total" v-for="item in tableTotals" :key="item.id">
          {{ item.total }}
        </td>
        <td class="total">{{ logs.length }}</td>
      </tr>
    </table>
  </div>
</template>

<script>
import { Option } from '@/models/Specialty'
import { mapGetters } from 'vuex'
import helpers from '../../mixins/statsHelperFunc'
import _ from 'lodash'

export default {
  props: {
    logs: { default: [] },
    logbookType: String
  },
  mixins: [helpers],
  data() {
    return {
      style: {
        borders: true,
        borderColor: '#cccccc',
        colors: ['#ffffff']
      },
      fetchingLogs: false,
      SurgeryDiagnosisOrm: [],
      SurgeryProcedureOrm: [],
      tableItems: [
        { id: 'surgeon', label: 'Cirurgião', component: 'autonomy', total: 0 },
        { id: 'helper', label: 'Ajudante', component: 'autonomy', total: 0 },
        { id: 'observer', label: 'Observador', component: 'autonomy', total: 0 }
      ]
    }
  },
  computed: {
    ...mapGetters(['user']),
    userAnatomicalGroups() {
      let aGroups = []

      const sMembers = this.superiorMembers.filter(sm =>
        this.logs.some(log => log['anatomic-region'] === sm)
      )

      if (sMembers.length) {
        aGroups.push({
          id: 'upperMember',
          text: 'Membro superior',
          options: sMembers
        })
      }

      const iMembers = this.inferiorMembers.filter(sm =>
        this.logs.some(log => log['anatomic-region'] === sm)
      )

      if (iMembers.length) {
        aGroups.push({
          id: 'lowerMember',
          text: 'Membro inferior',
          options: iMembers
        })
      }

      const eaMembers = this.axialSkeleton.filter(sm =>
        this.logs.some(log => log['anatomic-region'] === sm)
      )

      if (eaMembers.length) {
        aGroups.push({
          id: 'axialSkeleton',
          text: 'Esqueleto axial',
          options: eaMembers
        })
      }

      return aGroups.map(ag => ({
        ...ag,
        options: ag.options.map(m =>
          this.anatomicalRegions.find(o => m === o.value)
        )
      }))
    },
    superiorMembers() {
      return ['shoulder', 'arm', 'elbow', 'forearm', 'wrist', 'hand']
    },
    inferiorMembers() {
      return ['hip', 'thigh', 'knee', 'leg', 'ankle', 'foot']
    },
    axialSkeleton() {
      return ['spine', 'pelvic-ring']
    },
    components() {
      return this.allStatsComponents(this.logbookType)
    },
    anatomicalRegions() {
      return Option.query().where('component', 'anatomic-region').get()
    },
    userAnatomicalRegions() {
      return this.anatomicalRegions.filter(item =>
        this.logs.find(log => log['anatomic-region'] === item.value)
      )
    },
    tableTotals() {
      this.tableItems.forEach(item => {
        item.total = this.logs.filter(
          l => this.getAutonomy(l) === item.id
        ).length
      })
      return this.tableItems
    },
    cssVars() {
      return {
        '--bg-color': this.style.colors[0],
        '--bg-light-color': this.style.colors[0] + 20,
        '--bg-odd-color': this.style.colors[0] + 40,
        '--categ-color': this.style.colors[0] + 99,
        '--subcateg-color': this.style.colors[0] + 60,
        '--border-collapse': this.style.borders ? 'collapse' : 'inherit',
        '--border-color': this.style.borderColor,
        '--accent-border-color': this.style.borderColor + 50
      }
    }
  },
  methods: {
    getAutonomy(log) {
      if (
        log['surgeon'] === this.user.id ||
        log['surgeon'] === this.user.license
      ) {
        return 'surgeon'
      }
      if (
        log['helper-1'] === this.user.id ||
        log['helper-1'] === this.user.license
      ) {
        return 'helper'
      }
      if (
        log['helper-2'] === this.user.id ||
        log['helper-2'] === this.user.license
      ) {
        return 'helper'
      }
      if (
        log['helper-3'] === this.user.id ||
        log['helper-3'] === this.user.license
      ) {
        return 'helper'
      }
      if (
        log['observer'] === this.user.id ||
        log['observer'] === this.user.license
      ) {
        return 'observer'
      }
    },
    getAnatomicGroup(log) {
      const anatomicRegion = log['anatomic-region']

      const group = this.userAnatomicalGroups.find(uag =>
        uag.options.find(o => o.value === anatomicRegion)
      )

      return group.id
    },
    getGroupCount(groupId, autonomyId) {
      const groupLogs = this.logs.filter(
        l =>
          this.getAnatomicGroup(l) === groupId &&
          this.getAutonomy(l) === autonomyId
      )

      return groupLogs.length
    },
    getGroupTotal(groupId) {
      const groupLogs = this.logs.filter(
        l => this.getAnatomicGroup(l) === groupId
      )

      return groupLogs.length
    },
    getValue(autonomy, anatomicRegion) {
      return (
        this.logs.filter(l => {
          return (
            this.getAutonomy(l) === autonomy &&
            l['anatomic-region'] &&
            l['anatomic-region'] === anatomicRegion
          )
        }).length || 0
      )
    },
    getTotal(anatomicRegion) {
      return (
        this.logs.filter(l => {
          return l['anatomic-region'] && l['anatomic-region'] === anatomicRegion
        }).length || 0
      )
    }
  },
  created() {},
  mounted() {
    this.$root.$emit('isTable', true)
    this.$root.$on('selectedStyle', style => {
      this.style = style
    })
  }
}
</script>

<style>
.diagnose .v-messages__message {
  color: red;
}

.styled-table {
  font-size: 0.9em;
  font-family: sans-serif;
  width: 100%;
}
.styled-table.border td,
.styled-table.bordered thead {
  border-bottom: 1px solid var(--border-color);
}
.styled-table.bordered tr td,
.styled-table.border thead th {
  border-right: 1px solid var(--accent-border-color);
}
.styled-table.bordered tr td:last-child,
.styled-table.bordered thead th:last-child {
  border-right: none;
}
.categ-title {
  background-color: var(--categ-color) !important;
  width: 20px;
  font-weight: bold;
}
.divider {
  text-align: left !important;
  padding: 5px 15px !important;
}
.subcateg-title {
  background-color: var(--subcateg-color) !important;
}
.rotate-text {
  writing-mode: vertical-lr;
  -ms-writing-mode: tb-rl;
  transform: rotate(180deg);
}
.combined-report thead th.header-title {
  background-color: var(--categ-color) !important;
}
.combined-report thead th.total {
  background-color: var(--bg-color) !important;
}
.styled-table th,
.styled-table td {
  padding: 12px 15px;
  text-align: center;
  vertical-align: middle;
}

.odd {
  background-color: var(--bg-odd-color);
}
.even {
  background-color: var(--bg-light-color);
}

tr.total td {
  background-color: var(--bg-odd-color);
}
tr.total td.odd {
  background-color: var(--subcateg-color);
}
.styled-table td.total {
  background-color: var(--subcateg-color);
  font-weight: bold;
  min-width: 70px;
}
.total-cell {
  background-color: var(--categ-color) !important;
  font-weight: bold;
}
.styled-table tr:last-of-type {
  border-bottom: 1px solid var(--border-color);
}
</style>
